import { useContext } from 'react'
import { useQuery } from 'react-query'
import { apiGet, apiPostQuery } from 'api'
import AuthContext from 'context/auth-context'

const getBucketFiles = async (token, municipalityId, params) => {
  const url = `admin/municipalities/${municipalityId}/bucket_files`
  return apiGet({ url, token, params })
}

const getBucketFileSample = async (token, municipalityId, params) => {
  const url = `admin/municipalities/${municipalityId}/bucket_files/sample`
  return apiGet({ url, token, params })
}

const runBucketFileQuery = async (token, municipalityId, params) => {
  const url = `admin/municipalities/${municipalityId}/bucket_files/query`
  return apiPostQuery({ url, token, params })
}

// `refetchOnWindowFocus: false` across these hooks: each call is a real S3
// API hit (list_objects_v2 / get_object range fetch / select_object_content),
// and re-tabbing back to the admin UI shouldn't quietly increase AWS spend.
// Admins can hit "Refresh" or re-Run the filter when they actually want
// fresh data.

export function useAdminMunicipalityBucketFiles({ municipalityId, bucket, prefix, enabled = true, options } = {}) {
  const token = useContext(AuthContext)
  return useQuery(
    ['admin-municipality-bucket-files', municipalityId, bucket, prefix || ''],
    () => getBucketFiles(token, municipalityId, { bucket, prefix }),
    { enabled: enabled && !!municipalityId && !!bucket, keepPreviousData: true, refetchOnWindowFocus: false, ...options }
  )
}

export function useAdminMunicipalityBucketFileSample({ municipalityId, bucket, fileKey, rows = 50, separator = null, enabled = true, options } = {}) {
  const token = useContext(AuthContext)
  return useQuery(
    ['admin-municipality-bucket-file-sample', municipalityId, bucket, fileKey, rows, separator],
    () => getBucketFileSample(token, municipalityId, { bucket, key: fileKey, rows, separator: separator || undefined }),
    { enabled: enabled && !!municipalityId && !!bucket && !!fileKey, refetchOnWindowFocus: false, ...options }
  )
}

export function useAdminMunicipalityBucketFileQuery({ municipalityId, bucket, fileKey, groups = [], outerConjunction = 'AND', limit = 100, separator = null, enabled = false, options } = {}) {
  const token = useContext(AuthContext)
  const nonEmptyGroups = (groups || []).filter((g) => Array.isArray(g.conditions) && g.conditions.length > 0)
  const hasValid = nonEmptyGroups.length > 0 && nonEmptyGroups.every((g) =>
    g.conditions.every((c) => c.column && c.operator)
  )
  return useQuery(
    ['admin-municipality-bucket-file-query', municipalityId, bucket, fileKey, groups, outerConjunction, limit, separator],
    () => runBucketFileQuery(token, municipalityId, {
      bucket, key: fileKey, groups, outer_conjunction: outerConjunction, limit, separator: separator || undefined
    }),
    { enabled: enabled && !!municipalityId && !!bucket && !!fileKey && hasValid, keepPreviousData: false, refetchOnWindowFocus: false, ...options }
  )
}
